/**
 * 채널별 시간 사실 — "무엇을 언제 쟀는가" 를 계산해 둔다. 품질을 판정하지 않는다.
 *
 * 채널마다 rate 와 행 수가 다른 것이 비동기 원본의 정상이다. 그래서 화면이 말해야 하는 것은
 * "행 수가 다르다" 가 아니라 **어느 구간을 함께 볼 수 있는가**와 **지금 시각에 각 채널이 어느
 * 표본을 보고 있는가**다. 시계가 다르거나 미확인이면 같은 시각으로 비교하지 않는다.
 *
 * Owns:
 * - 채널 coverage(구간 · 간격 · gap · 무효), 공통 구간, 비교 가능 상태, 현재 커서.
 *
 * Does not own:
 * - 표본 상태 판정 — `poseSampling.ts`. 그리기 — `page/`.
 */
import {
  medianIntervalSeconds,
  rowIndexAtSeconds,
  type InspectChannel,
  type InspectTimeOrigin,
} from "../domain/episodeInspect";

/** 기본 gap 배수 — 엔진(`hatchery-data`)의 timeline 분석과 같은 값이다. */
export const DEFAULT_GAP_MULTIPLE = 5;

export type TimeSpan = { startSeconds: number; endSeconds: number };

export type StreamCoverage = {
  key: string;
  clockId: string | null;
  tMin: number | null;
  tMax: number | null;
  rowCount: number;
  dtMedianSeconds: number | null;
  /** 중앙 간격의 배수를 넘는 구간 — 표본이 없는 자리다. */
  gapSpans: readonly TimeSpan[];
  /** 엔진이 무효로 표시한 행이 이어지는 구간. */
  invalidSpans: readonly TimeSpan[];
};

export type CommonWindow = {
  /** 모든 채널이 함께 있는 길이(초). 겹치지 않으면 0. */
  seconds: number;
  /** 가장 이른 시작부터 가장 늦은 끝까지(초). */
  spanSeconds: number;
  /** `seconds / spanSeconds`. 분모가 0 이면 0. */
  fraction: number;
  /** 몇 개 채널을 셌는가 — 분모를 숨기지 않는다. */
  streamCount: number;
};

export type Comparability =
  | { kind: "comparable" }
  | { kind: "clock_unknown"; streams: readonly string[] }
  | { kind: "clock_mismatch"; clockIds: readonly string[] }
  | { kind: "no_overlap" }
  | { kind: "empty" };

/** 채널 하나의 시간 사실. */
export function streamCoverage(
  channel: InspectChannel,
  gapMultiple = DEFAULT_GAP_MULTIPLE,
): StreamCoverage {
  const times = channel.secondsFromOrigin;
  const median = medianIntervalSeconds(channel);
  const limit = median === null ? Number.POSITIVE_INFINITY : median * gapMultiple;
  const gapSpans: TimeSpan[] = [];
  for (let index = 1; index < times.length; index += 1) {
    if (times[index] - times[index - 1] > limit) {
      gapSpans.push({ startSeconds: times[index - 1], endSeconds: times[index] });
    }
  }
  return {
    key: channel.key,
    clockId: channel.clockId,
    tMin: times[0] ?? null,
    tMax: times[times.length - 1] ?? null,
    rowCount: times.length,
    dtMedianSeconds: median,
    gapSpans,
    invalidSpans: invalidSpans(channel),
  };
}

/** 무효 행이 이어지는 구간 — 한 행짜리도 구간으로 센다(길이 0). */
function invalidSpans(channel: InspectChannel): TimeSpan[] {
  const times = channel.secondsFromOrigin;
  const spans: TimeSpan[] = [];
  let start: number | null = null;
  channel.validity.forEach((valid, index) => {
    if (!valid && start === null) {
      start = times[index];
    }
    if (valid && start !== null) {
      spans.push({ startSeconds: start, endSeconds: times[index - 1] ?? start });
      start = null;
    }
  });
  if (start !== null) {
    spans.push({ startSeconds: start, endSeconds: times[times.length - 1] ?? start });
  }
  return spans;
}

/** 모든 채널이 함께 있는 구간. */
export function commonWindow(coverages: readonly StreamCoverage[]): CommonWindow {
  const spans = coverages.filter(
    (coverage): coverage is StreamCoverage & { tMin: number; tMax: number } =>
      coverage.tMin !== null && coverage.tMax !== null,
  );
  if (spans.length === 0) {
    return { seconds: 0, spanSeconds: 0, fraction: 0, streamCount: 0 };
  }
  const latestStart = Math.max(...spans.map((span) => span.tMin));
  const earliestEnd = Math.min(...spans.map((span) => span.tMax));
  const earliestStart = Math.min(...spans.map((span) => span.tMin));
  const latestEnd = Math.max(...spans.map((span) => span.tMax));
  const seconds = Math.max(0, earliestEnd - latestStart);
  const spanSeconds = latestEnd - earliestStart;
  return {
    seconds,
    spanSeconds,
    fraction: spanSeconds > 0 ? seconds / spanSeconds : 0,
    streamCount: spans.length,
  };
}

/**
 * 같은 시각으로 비교해도 되는가.
 *
 * 시계 ID 가 없으면 관계를 모른다는 뜻이라 비교하지 않는다(엔진 `SourceClock` 계약). 서로 다른
 * 시계를 쓰면 숫자가 같아도 같은 순간이 아니다.
 */
export function comparability(
  channels: readonly InspectChannel[],
  origin: InspectTimeOrigin,
): Comparability {
  if (channels.length === 0) {
    return { kind: "empty" };
  }
  const unknown = channels.filter((channel) => channel.clockId === null);
  if (unknown.length > 0) {
    return { kind: "clock_unknown", streams: unknown.map((channel) => channel.key) };
  }
  const ids = [...new Set(channels.map((channel) => channel.clockId as string))];
  if (ids.length > 1) {
    return { kind: "clock_mismatch", clockIds: ids.sort() };
  }
  if (origin.value === null) {
    return { kind: "clock_unknown", streams: [] };
  }
  return commonWindow(channels.map((channel) => streamCoverage(channel))).seconds > 0
    ? { kind: "comparable" }
    : { kind: "no_overlap" };
}

export type StreamCursor = {
  key: string;
  /** 원본 파일 행 번호. `null` 이면 미확인이다. */
  row: number | null;
  /** 배열 안의 위치 — 값을 꺼낼 때 쓴다. */
  index: number;
  sampleTimeSeconds: number | null;
  /** 재생 시각과 그 표본 시각의 차이. 관측 시작 전이면 음수다. */
  deltaSeconds: number | null;
  valid: boolean;
};

/** 지금 시각에 채널이 보고 있는 표본. */
export function streamCursorAt(channel: InspectChannel, seconds: number): StreamCursor {
  const index = rowIndexAtSeconds(channel, seconds);
  const sampleTime = channel.secondsFromOrigin[index];
  return {
    key: channel.key,
    row: channel.sourceRows === null ? null : (channel.sourceRows[index] ?? null),
    index,
    sampleTimeSeconds: sampleTime ?? null,
    deltaSeconds: sampleTime === undefined ? null : seconds - sampleTime,
    valid: channel.validity[index] !== false,
  };
}
